// The ONE place a marketing figure becomes words. Both the hero strip and the Meta Ads KPI strip read the same
// four metrics off the same account; two renderings of one number is how a page comes to disagree with itself
// (`holdCountdown`, `trendingCopy` — the same rule, one surface over).
//
// Every formatter returns the legacy `-` (not an em dash) for an absent value, matching the specimen's markup:
// the tile's own empty SUB-LINE is what says WHY it is absent, and that split is load-bearing — see
// `paidEmptySub`. A real ZERO renders as "0" / "$0" / "0.00%" (PO, ART-288): legacy's "0 renders as absent" rule
// made a connected account with no spend this month read exactly like one we hold no data for. `-` is kept for
// null/undefined/non-finite (we have no figure) and for a negative, which no count or spend can honestly be.

/** Compact number → "214.6k" / "1.2M"; 0 → "0". Legacy `fmtCompact`, minus its "0 renders as absent" rule. */
export function compactOrDash(n: number | null | undefined): string {
  if (n == null) return "-";
  const num = Number(n);
  if (!Number.isFinite(num) || num < 0) return "-";
  if (num < 1000) return String(Math.round(num));
  if (num < 1e6) {
    const k = num / 1000;
    return `${k >= 10 ? k.toFixed(1) : k.toFixed(2)}k`;
  }
  const m = num / 1e6;
  return `${m >= 10 ? m.toFixed(1) : m.toFixed(2)}M`;
}

/** Exact count with thousands separators — for a figure a reader is meant to be able to read back ("6,420"). */
export function exactOrDash(n: number | null | undefined): string {
  if (n == null) return "-";
  const num = Number(n);
  return Number.isFinite(num) && num >= 0 ? Math.round(num).toLocaleString() : "-";
}

/** Money from MINOR units → "$1,840" / "$3.10". Legacy `fmtMoney`: sub-100 keeps its cents, above it rounds. */
export function moneyOrDash(minorUnits: number | null | undefined, currency = "USD"): string {
  if (minorUnits == null) return "-";
  const num = Number(minorUnits);
  if (!Number.isFinite(num) || num < 0) return "-";
  const major = num / 100;
  const symbol = currency === "USD" ? "$" : "";
  return major < 100
    ? `${symbol}${major.toFixed(2).replace(/\.00$/, "")}`
    : `${symbol}${Math.round(major).toLocaleString()}`;
}

/** A percent to 2dp → "1.25%". */
export function pctOrDash(n: number | null | undefined): string {
  if (n == null) return "-";
  const num = Number(n);
  return Number.isFinite(num) && num >= 0 ? `${num.toFixed(2)}%` : "-";
}

/** Click-through rate from the raw pair. Null impressions ⇒ absent: a CTR over no impressions is unmeasured,
 * never 0%. */
export function ctr(clicks: number | null | undefined, impressions: number | null | undefined): number | null {
  return clicks != null && impressions != null && impressions > 0 ? (clicks / impressions) * 100 : null;
}

/** Cost per click, in minor units. Legacy renders this under the label "Cost per click" — see the note in
 * `AdsManager` on why the spec's "Cost · stream" is not used. */
export function costPerClick(spendMinor: number | null | undefined, clicks: number | null | undefined): number | null {
  return spendMinor != null && clicks != null && clicks > 0 ? spendMinor / clicks : null;
}

/** The combined "Clicks / CTR" reading — "6,420 · 1.25%". Absent when neither half has a value, because a bare
 * "· 1.25%" reads as a broken template rather than as a partial measurement. */
export function clicksAndCtr(clicks: number | null | undefined, impressions: number | null | undefined): string {
  const rate = ctr(clicks, impressions);
  if (clicks == null && rate == null) return "-";
  return `${exactOrDash(clicks)} · ${pctOrDash(rate)}`;
}

/**
 * The hero tile's empty sub-line, and the reason this module exists at all.
 *
 * **Not-connected and no-spend are DIFFERENT empties and must never collapse** (the spec is explicit, and the
 * codebase already carries the same rule twice — "connected-with-no-figure is a REAL state", and the
 * connections card's unreachable error line). A band with no Meta connection has something to DO; a connected
 * band that simply did not run ads last month does not, and telling it to go connect an account it already has
 * is the failure both of those bullets are about.
 *
 * Returns null for the third case legacy could not express: connected, but we have never measured this band's
 * account. Silence there is honest — a sentence would have to claim one of the other two.
 */
export function paidEmptySub(metaConnected: boolean, hasAdAccount: boolean): string | null {
  if (!metaConnected) return "No ad account connected";
  return hasAdAccount ? "No spend in last 30d" : null;
}

/** Signed percent → "+18%" / "−3%" (a real minus sign, as legacy renders). Null when there is no comparison —
 * the tile then shows no arrow rather than a 0% that reads as "flat". */
export function deltaLabel(n: number | null | undefined): string | null {
  if (n == null || !Number.isFinite(Number(n))) return null;
  const v = Number(n);
  return `${v >= 0 ? "+" : "−"}${Math.abs(Math.round(v))}%`;
}

/** A campaign's flight line — "Aug 1 - ongoing" / "Jul 2 - Jul 30" / "Not started". */
export function flightDates(startTime: string | null | undefined, stopTime: string | null | undefined): string {
  const start = shortDate(startTime);
  const stop = shortDate(stopTime);
  if (start && stop) return `${start} - ${stop}`;
  if (start) return `${start} - ongoing`;
  return "Not started";
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** The campaigns card's count line. Not connected ⇒ legacy's placeholder verbatim — it keeps the line's SHAPE
 * on the page so the card does not visibly grow a row the moment a band connects. */
export function campaignCountLine(
  counts: { active?: number; paused?: number; ended?: number; draft?: number } | null | undefined,
): string {
  if (!counts) return "active ·, paused ·, ended ·, draft";
  return `${counts.active ?? 0} active · ${counts.paused ?? 0} paused · ${counts.ended ?? 0} ended · ${counts.draft ?? 0} draft`;
}
